import dayjs from 'dayjs';
import { StorageContainer } from '../../../../../services/storage';
import { StorageContainerLabelData, IStorageLabelAdapter } from '../types/storageLabel';

/**
 * 仓储容器标签适配器
 * 将StorageContainer数据转换为标签数据格式
 */
export class StorageContainerAdapter implements IStorageLabelAdapter<StorageContainer> {
  
  /**
   * 转换仓储容器数据为标签数据
   */
  transform(
    container: StorageContainer,
    options?: {
      contentSummary?: { 
        totalItems: number; 
        itemTypes: string[]; 
        totalQuantity: number; 
      };
      printOptions?: { 
        copies: number; 
        directPrint: boolean; 
      };
      templateId?: string;
    }
  ): StorageContainerLabelData {
    
    console.log('[仓储容器适配器] 开始转换容器数据:', {
      container,
      options
    });

    // 解析仓库编码（如果warehouse_name包含编码信息）
    const warehouseCode = this.extractWarehouseCode(container.warehouse_name);
    
    // 优先使用后端返回的项目编码；缺失时再从项目名称中解析
    const projectCode = String((container as any)?.project_code || '').trim() || this.extractProjectCode(container.project_name);

    const result: StorageContainerLabelData = {
      id: `storage-container-${container.id}`,
      templateId: options?.templateId || 'storage-container-4.25x1.25',
      containerInfo: {
        id: container.id,
        systemCode: container.system_code,
        name: container.name,
        type: container.container_type,
        typeDisplay: container.container_type_display,
        status: container.status,
        statusDisplay: container.status_display,
        warehouse: {
          id: container.warehouse,
          name: container.warehouse_name,
          code: warehouseCode
        },
        project: {
          id: container.project,
          name: container.project_name,
          code: projectCode
        },
        location: container.current_location ? {
          id: container.current_location,
          name: `位置-${container.current_location}`,
          code: `LOC-${container.current_location}`
        } : undefined,
        createdBy: container.created_by_username,
        createdAt: this.formatDateTime(container.updated_at),
        updatedAt: container.updated_at,
        remarks: String((container as any)?.remarks || '').trim()
      },
      contentSummary: options?.contentSummary,
      printOptions: options?.printOptions || {
        copies: 1,
        directPrint: true
      }
    };

    console.log('[仓储容器适配器] 转换后的标签数据:', result);
    return result;
  }

  /**
   * 验证标签数据的完整性
   */
  validate(data: StorageContainerLabelData): boolean {
    const required = ['id', 'templateId', 'containerInfo'];
    const isValid = required.every(field => data[field] !== undefined);
    
    if (!isValid) {
      console.error('[仓储容器适配器] 标签数据验证失败:', data);
      return false;
    }

    // 验证容器信息的必填字段
    const containerInfoRequired = ['id', 'systemCode', 'name', 'warehouse', 'project'];
    const containerInfoValid = containerInfoRequired.every(
      field => data.containerInfo[field] !== undefined
    );

    if (!containerInfoValid) {
      console.error('[仓储容器适配器] 容器信息验证失败:', data.containerInfo);
      return false;
    }

    return true;
  }

  /**
   * 批量转换容器数据
   */
  batchTransform(
    containers: StorageContainer[],
    options?: {
      contentSummaryMap?: Map<number, { totalItems: number; itemTypes: string[]; totalQuantity: number; }>;
      printOptions?: { copies: number; directPrint: boolean; };
      templateId?: string;
    }
  ): StorageContainerLabelData[] {
    return containers.map(container => {
      const contentSummary = options?.contentSummaryMap?.get(container.id);
      return this.transform(container, {
        ...options,
        contentSummary
      });
    });
  }

  /**
   * 从仓库名称中提取仓库编码
   */
  private extractWarehouseCode(warehouseName: string): string {
    // 假设仓库名称格式为 "US-1-NH 新罕布什尔仓库" 或类似格式
    const match = warehouseName.match(/^([A-Z0-9-]+)/);
    return match ? match[1] : warehouseName;
  }

  /**
   * 从项目名称中提取项目编码
   */
  private extractProjectCode(projectName: string): string {
    // 假设项目名称可能包含编码信息
    // 这里可以根据实际的项目命名规则进行调整
    const match = projectName.match(/^([A-Z0-9-]+)/);
    return match ? match[1] : projectName.substring(0, 10); // 如果没有编码，取前10个字符
  }

  /**
   * 格式化日期时间为标签显示格式
   */
  private formatDateTime(dateTimeString: string): string {
    try {
      return dayjs(dateTimeString).format('MM/DD HH:mm');
    } catch (error) {
      console.warn('[仓储容器适配器] 日期格式化失败:', dateTimeString, error);
      return dateTimeString.substring(0, 16); // 返回前16个字符作为fallback
    }
  }

  /**
   * 根据容器类型获取默认模板ID
   */
  getDefaultTemplateId(containerType: string): string {
    // 可以根据容器类型返回不同的模板
    switch (containerType) {
      case 'small_box':
      case 'envelope':
        return 'storage-container-3x1';
      default:
        return 'storage-container-4.25x1.25';
    }
  }

  /**
   * 创建内容摘要
   */
  createContentSummary(items: Array<{ name: string; quantity: number; type?: string }>): {
    totalItems: number;
    itemTypes: string[];
    totalQuantity: number;
    items?: Array<{ code: string; quantity: number }>; // 新增：详细的库存单元列表
  } {
    const totalItems = items.length;
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    
    // 使用Array.from替代展开语法来避免TypeScript编译错误
    const uniqueTypes = new Set(items.map(item => item.type || item.name));
    const itemTypes = Array.from(uniqueTypes);

    return {
      totalItems,
      itemTypes: itemTypes.slice(0, 3), // 最多显示3种类型
      totalQuantity
    };
  }
}

// 导出单例实例
export const storageContainerAdapter = new StorageContainerAdapter();
